import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Icon } from "./icon";
import { toast } from "./toaster";

const COPIED_RESET_MS = 2000;

/** Clipboard copy with a transient `copied` flag. Clipboard access can be denied: never throws. */
export function useCopy() {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => {
    clearTimeout(timer.current);
  }, []);

  const copy = useCallback(async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return false;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCopied(false);
    }, COPIED_RESET_MS);
    return true;
  }, []);

  return { copied, copy };
}

export interface CopyButtonProps {
  value: string;
  label?: string;
  copiedLabel?: string;
  failedLabel?: string;
  className?: string;
}

export function CopyButton({
  value,
  label = "Copy",
  copiedLabel = "Copied",
  failedLabel = "Couldn't copy — select the text and copy it manually",
  className,
}: CopyButtonProps) {
  const { copied, copy } = useCopy();
  const handleClick = () => {
    void copy(value).then((ok) => {
      if (ok) toast.success(copiedLabel);
      else toast.error(failedLabel);
    });
  };
  return (
    <Button variant="ghost" size="sm" iconOnly aria-label={copied ? copiedLabel : label} className={className} onClick={handleClick}>
      <Icon name={copied ? "check" : "copy"} />
    </Button>
  );
}

export interface CodeBlockProps {
  code: string;
  /** Accessible name for the code region, e.g. "Install snippet". */
  label?: string;
  className?: string;
  copyLabel?: string;
  copiedLabel?: string;
}

export function CodeBlock({ code, label, className, copyLabel, copiedLabel }: CodeBlockProps) {
  return (
    <div className={cn("relative", className)}>
      {/* A long line scrolls sideways: the block must take focus so the keyboard can scroll it (WCAG 2.1.1, axe scrollable-region-focusable). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <pre className="code pr-12" aria-label={label} tabIndex={0}>
        <code>{code}</code>
      </pre>
      <CopyButton
        value={code}
        label={copyLabel}
        copiedLabel={copiedLabel}
        className="absolute right-2 top-2 text-ink-300 hover:bg-ink-800 hover:text-white"
      />
    </div>
  );
}
